import { RISK_LEVEL } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import styled from 'styled-components/macro'
import palette from 'src/designSystem/variables/palette'
import { BadgeSuccessSVG, BadgeErrorSVG } from 'src/components/Icons/Basic'
import { StickyGridHeader } from 'src/components/Reusable/Grid/Grid.styles'
import { Dropdown } from 'react-bootstrap'
import {
  RISK_LEVEL_TO_BORDER_COLOR,
  RISK_LEVEL_TO_COLOR,
} from './vendors.constants'

export const RiskLevelChip = styled.div<{ $riskLevel: RISK_LEVEL }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5em 0.75em;
  border-radius: 0.5em;
  font-size: 0.95em;
  text-wrap: nowrap;
  background-color: ${({ $riskLevel }) => RISK_LEVEL_TO_COLOR[$riskLevel]};
`

// The risk tier pill from the design: the tier's fill with a darker outline of
// the same hue, carrying the full "Tier 3: Low risk exposure" label. Shared by
// the dropdown's toggle and by each option inside it, so the thing you pick and
// the thing you picked are the same object.
//
// 24px rather than the design's 19px: 19px is the text box, and a control that
// short is an awkward click target.
export const RiskTierPill = styled.div<{ $riskLevel: RISK_LEVEL }>`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  min-height: 24px;
  padding: 2px 12px;
  border-radius: 20px;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.15px;
  white-space: nowrap;
  color: ${palette.neutral[800]};
  background-color: ${({ $riskLevel }) => RISK_LEVEL_TO_COLOR[$riskLevel]};
  border: 1px solid
    ${({ $riskLevel }) => RISK_LEVEL_TO_BORDER_COLOR[$riskLevel]};
`

// The dropdown's closed state: the pill, plus room for the chevron. Unstyled as
// a button so the pill itself is the whole control.
export const RiskTierPillToggle = styled(RiskTierPill).attrs({
  as: 'button',
})<{ $riskLevel: RISK_LEVEL }>`
  gap: 4px;
  padding-right: 4px;
  cursor: pointer;

  /* react-bootstrap's Dropdown.Toggle draws its own caret; this pill brings
     the design's chevron, so hide theirs rather than showing two. */
  &::after {
    display: none;
  }

  &:disabled {
    cursor: default;
  }

  svg {
    flex-shrink: 0;
  }
`

export const AttestationStatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5em;
  font-size: 0.95em;
`

export const AttestationFreshIcon = styled(BadgeSuccessSVG)`
  height: 16px;
  width: 16px;
`

export const AttestationStaleIcon = styled(BadgeErrorSVG)`
  height: 18px;
  width: 18px;
  position: relative;
  left: -1px;
  path {
    fill: ${palette.magenta[400]};
  }
`

export const StyledVendorsGridHeader = styled(StickyGridHeader)<{
  isCentered?: boolean
}>`
  ${({ isCentered }) => isCentered && `justify-content: center;`}
`
export const SectionDivider = styled.hr`
  margin: 0 0 1em 0;
`

// Add Vendor modal: Status at half width, with the short Active/Inactive
// State picker beside it.
export const AddVendorStatusStateRow = styled.div`
  display: flex;
  gap: 16px;
`

export const AddVendorStatusCell = styled.div`
  flex: 0 0 50%;
  min-width: 0;
`

export const AddVendorStateCell = styled.div`
  flex: 0 0 140px;
`

export const RiskTierTitle = styled.div`
  font-size: 14px;
`

export const RiskTierInfoContainer = styled.div`
  font-size: 14px;
  margin: 0.25em 0 1em 0;
`

export const RiskTierOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  font-size: 14px;
  margin-left: 0.5em;
`

export const RiskTierRadioButton = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
`

export const RiskTierError = styled.div`
  color: ${palette.magenta[500]};
  font-size: 0.9em;
  margin-top: 0.5em;
  height: 1.5em;
`

export const VendorTab = styled.div`
  padding-bottom: 0.5em;

  .vendor-tab-btn {
    margin-top: 1em;
  }
`

export const TierSummary = styled.p`
  margin-top: 1em;
`

export const VendorToggle = styled(Dropdown.Toggle)<{ $bgColor: string }>`
  border: none;
  color: ${palette.neutral.black};
  background-color: ${({ $bgColor }) => $bgColor};

  &::after {
    margin-left: 0.5em;
  }

  &.btn-primary.dropdown-toggle {
    border: none;
    color: ${palette.neutral.black};
    background-color: ${({ $bgColor }) => $bgColor};
  }
`

export const VendorOwnerLabel = styled.label`
  margin-left: 8px;
`

// Shared by the index page's tab bodies: only the tab-nav row gets the page's
// horizontal padding, so a tab's content is full-bleed and has to supply its
// own or it sits left of the tabs above it.
export const FullBleedTabBody = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  width: 100%;
  padding: 0 24px;
`
